@if (isSubmitting()) {
  <div class="spinner">
    <geaui-progress-spinner [size]="'large'"></geaui-progress-spinner>
  </div>
}
<geaui-page-content-layout-v3>
  <ng-container page-header>
    <geaui-page-header-v3 [hasBackArrow]="false" [withSidebar]="true" pageTitle="Data Management">
      <div class="flex justify-content-between align-items-center">
        <div class="workspace-repository-controls">
          <app-workspace-popover
            [worktrees]="workspaceWorktrees"
            [repositories]="repositoryOptions"
            [activeWorktree]="activeWorktree()"
            [isLoadingWorkspaces]="isLoadingWorkspaces()"
            [isCreatingWorkspace]="isCreatingWorkspace()"
            [isCopyingWorkspace]="isCopyingWorkspace()"
            [deletingWorkspace]="checkingDeleteWorkspace()"
            [remoteWorkspaceOptions]="remoteWorkspaceOptions"
            [selectedRemoteWorkspace]="selectedRemoteWorkspace"
            (createWorkspace)="onCreateWorkspace($event)"
            (selectWorkspace)="onSelectWorkspace($event)"
            (copyWorkspace)="onCopyWorkspace($event)"
            (deleteWorkspace)="onDeleteWorkspace($event)">
          </app-workspace-popover>
          <div class="repository-selector">
            <geaui-button-v3 [size]="'small'" (click)="toggleRepositoryPopover($event)" type="secondary">
              <div class="repository-button-content">
                <span>{{ selectedRepository()?.name || 'Repository' }}</span>
                @if (selectedRepository() && !selectedRepositoryCanAccess()) {
                  <i class="pi pi-lock" title="View only"></i>
                }
                <i class="pi pi-chevron-down"></i>
              </div>
            </geaui-button-v3>
            <p-popover #repositoryPopover appendTo="body" styleClass="no-arrow-popover repository-popover">
              <div class="repository-popover-content">
                @for (repository of repositoryOptions; track repository.value) {
                  <button
                    class="repository-option"
                    [class.repository-option-selected]="repository.value === selectedRepository()?.value"
                    (click)="selectRepository(repository)"
                    type="button">
                    <span>{{ repository.name }}</span>
                    @if (!repositoryCanAccess(repository)) {
                      <span class="repository-access-status"><i class="pi pi-lock"></i> View only</span>
                    }
                  </button>
                }
              </div>
            </p-popover>
          </div>
        </div>
        <div class="flex align-items-center gap-4 dm-header-actions">
          <div class="search-wrapper">
            <geaui-search-bar-v3
              [id]="'search-bar'"
              [placeholder]="'Search assets...'"
              [hasSuggestions]="false"
              (valueChange)="searchValueChanged($event)"
              (clearEvent)="searchValueChanged('')"
              (searchButtonClicked)="onSearchButtonClicked()">
            </geaui-search-bar-v3>
          </div>
          <geaui-button-v3
            [disabled]="validateAndEnableAddButton()"
            [preIcon]="'e-add'"
            [type]="'secondary'"
            (click)="onFileUpload()"
            >Add File</geaui-button-v3
          >

          <app-workspace-options-popover
            [options]="resolvedWorkspaceOptions()"
            [buttonDisabled]="validateAndEnableWorkspaceOptions()"
            [itemsDisabled]="!activeWorktree"
            [uncommittedCount]="uncommitedUserAssets()">
          </app-workspace-options-popover>
        </div>
      </div>
    </geaui-page-header-v3>
  </ng-container>

  <ng-container page-content>
    <div class="dm-content-layout">
      <p-splitter [style]="{ height: '100%' }" [panelSizes]="[20, 80]" [minSizes]="[10, 0]">
        <ng-template pTemplate="panel">
          <!-- LEFT SIDEBAR -->
          @if (isLoadingConfig()) {
            <div class="assets-sidebar p-2 pt-5">
              @for (num of [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]; track num) {
                <p-skeleton width="100%" height="2rem" styleClass="mb-2"></p-skeleton>
              }
            </div>
          } @else {
            <div class="assets-sidebar">
              <p-tree
                class="p-0 w-full"
                [(selection)]="selectedNode"
                [value]="assetTree()"
                (onNodeSelect)="onNodeSelect($event.node)"
                (onNodeUnselect)="onNodeUnselect()"
                selectionMode="single">
                <ng-template pTemplate="default" let-node>
                  <span [pTooltip]="node.label" [class]="node.styleClass" tooltipPosition="top">
                    <i
                      class="p-tree-node-icon pi pi-fw"
                      [class.pi-spin]="node.key === loadingNodeKey()"
                      [class.pi-spinner]="node.key === loadingNodeKey()"
                      [class.pi-folder-open]="node.key !== loadingNodeKey()"></i>
                    <span class="ml-2">{{ node.label }}</span>
                  </span>
                </ng-template>
              </p-tree>
            </div>
          }
        </ng-template>
        <ng-template pTemplate="panel">
          <!-- RIGHT MAIN CONTENT AREA -->
          <div class="dm-main-content">
            @if (isSearchMode()) {
              <!-- Search Results View -->
              <div class="search-results-wrapper">
                <div class="gea-font-heading-24">Search Results</div>
                @if (isLoadingSearchResults()) {
                  <div class="right-content-spinner">
                    <geaui-progress-spinner [size]="'medium'"></geaui-progress-spinner>
                  </div>
                } @else if (searchResults().length === 0) {
                  <div class="p-4 text-center gea-font-heading-24">
                    <p>No results found...</p>
                  </div>
                } @else {
                  <div class="search-results-list" (scroll)="onSearchResultsScroll($event)">
                    @for (result of searchResults(); track result.path; let last = $last) {
                      <div class="search-result-item">
                        <div class="search-result-filename" (click)="onSearchResultClick(result)">
                          <i class="pi" [class.pi-folder]="result.isFolder" [class.pi-file]="!result.isFolder"></i>
                          <span [innerHTML]="highlightSearchTerm(result.fileName)"></span>
                        </div>
                        <p-breadcrumb [model]="getSearchResultBreadcrumb(result)" [home]="{ icon: 'pi pi-home' }"></p-breadcrumb>
                      </div>
                      @if (!last) {
                        <p-divider></p-divider>
                      }
                    }
                    @if (isLoadingMoreSearchResults()) {
                      <div class="search-results-loading-more">
                        <geaui-progress-spinner [size]="'medium'"></geaui-progress-spinner>
                      </div>
                    }
                  </div>
                }
              </div>
            } @else {
              <!-- Normal Table View -->
              <div class="assets-table-wrapper geaui-sidebar-grid geaui-scrollable-table">
                <div class="dm-folderName">
                  {{ selectedFolder }}
                </div>
                <geaui-table-v3
                  [tableId]="tableId"
                  [columnDefinitions]="columnDefinitions"
                  [data]="assetTableData"
                  [noEntriesProcessPageInfoText]="noResultsInfoText"
                  [totalRecords]="count"
                  [loading]="isLoadingAssets()"
                  [rowsClickable]="true"
                  [sortable]="true"
                  [isLandingPage]="false"
                  [noEntriesInfoText]="'No assets available yet'"
                  [noEntriesActionText]="'Upload assets'"
                  [noEntriesActionIcon]="'e-add'"
                  (rowClicked)="onRowClicked($event)">
                </geaui-table-v3>
              </div>
            }
          </div>
        </ng-template>
      </p-splitter>
    </div>
  </ng-container>
</geaui-page-content-layout-v3>

<input #fileInput (change)="validateFileSelection($event)" type="file" style="display: none" />

@if (loading()) {
  <div class="spinner">
    <geaui-progress-spinner [size]="'large'"></geaui-progress-spinner>
  </div>
}

@if (isPreviewOpen()) {
  <app-file-preview-sidebar
    [asset]="previewAsset()"
    [canMutate]="selectedRepositoryCanAccess()"
    [allowUpload]="previewAssetAllowUpload()"
    [canReplace]="previewAssetCanReplace()"
    (close)="closePreviewSidebar()"
    (undoChanges)="handleUndoChanges($event)"
    (replace)="handleSidebarReplace($event)"
    (rename)="handleSidebarRename($event)"
    (download)="handleSidebarDownload($event)"
    (delete)="handleSidebarDelete($event)">
  </app-file-preview-sidebar>
}
